import { X } from 'lucide-react';
import { Toast as ToastPrimitive } from 'radix-ui';
import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { t } from '../../i18n';
import { Icon } from '../icons';
import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import { tones, type Tone } from './tones';

type ToastInput = {
  tone?: Tone;
  title: string;
  description?: ReactNode;
  traceId?: string;
  showTraceId?: boolean;
  action?: { label: string; onClick: () => void };
  durationMs?: number;
};

type ToastApi = { toast: (input: ToastInput) => string; dismiss: (id: string) => void };

type ToastEntry = ToastInput & { id: string; open: boolean };

const ToastContext = createContext<ToastApi | null>(null);

// On phones above the bottom nav and the composer, whose heights AppShell and Composer publish, so a toast never
// covers them (design system §4 Toast).
const viewportStyle = {
  '--toast-bottom':
    'calc(var(--nav-inset, 0px) + var(--composer-inset, 0px) + calc(var(--spacing) * 4))',
} as CSSProperties;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const lastId = useRef(0);
  const api = useMemo<ToastApi>(
    () => ({
      toast: (input) => {
        lastId.current += 1;
        const id = String(lastId.current);
        // A closed toast stays until the next one, so its exit motion can play.
        setToasts((list) => [...list.filter((entry) => entry.open), { ...input, id, open: true }]);
        return id;
      },
      dismiss: (id) =>
        setToasts((list) =>
          list.map((entry) => (entry.id === id ? { ...entry, open: false } : entry)),
        ),
    }),
    [],
  );

  return (
    <ToastContext value={api}>
      <ToastPrimitive.Provider label={t('components.Toast.label')}>
        {children}
        {toasts.map(({ id, open, ...input }) => (
          <ToastItem key={id} input={input} open={open} onClose={() => api.dismiss(id)} />
        ))}
        {/* aria-live=off is the region's default, written so that a modal Sheet or Dialog's hideOthers keeps the
            toasts reachable (aria-hidden #10). */}
        <ToastPrimitive.Viewport
          label={t('components.Toast.region')}
          aria-live="off"
          style={viewportStyle}
          className="fixed right-0 bottom-(--toast-bottom) left-0 z-(--z-toast) flex flex-col gap-2 px-4 focus-ring md:top-4 md:right-4 md:bottom-auto md:left-auto md:w-full md:max-w-sm md:px-0"
        />
      </ToastPrimitive.Provider>
    </ToastContext>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast() needs a <ToastProvider> above it.');
  return api;
}

function ToastItem({
  input,
  open,
  onClose,
}: {
  input: ToastInput;
  open: boolean;
  onClose: () => void;
}) {
  const { tone = 'info', title, description, traceId, showTraceId, action, durationMs } = input;
  const { icon, className } = tones[tone];
  const [copied, setCopied] = useState(false);

  return (
    <ToastPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      // A toast with a trace id stays until closed, so Hata kodu is still there for support (WCAG 2.2.1).
      duration={traceId ? Infinity : durationMs}
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 shadow-lg focus-ring data-[state=open]:animate-enter data-[state=closed]:animate-exit',
        className,
      )}
    >
      <Icon icon={icon} className="shrink-0" />
      {/* Radix announces the toast's text nodes joined with nothing between them; the spaces keep the words apart
          (a grid or flex box does not render them). */}
      <div className="grid min-w-0 flex-1 justify-items-start gap-1 text-sm wrap-anywhere">
        <ToastPrimitive.Title className="font-semibold">{title}</ToastPrimitive.Title>{' '}
        {description && <ToastPrimitive.Description>{description}</ToastPrimitive.Description>}{' '}
        {traceId && (
          <div className="flex flex-wrap items-center gap-2">
            {/* The X-Trace-Id of the failed call, for support (merchant screens §4.2, admin screens §4.4). */}
            <Button
              variant="link"
              size="sm"
              aria-label={t('components.Toast.copyTrace')}
              className="px-0 text-current underline"
              onClick={() =>
                void navigator.clipboard.writeText(traceId).then(() => setCopied(true))
              }
            >
              {t('components.Toast.traceCode')}
            </Button>{' '}
            {showTraceId && <span className="font-mono">{traceId}</span>}
            {/* Mounted empty, so a screen reader hears the change to Kopyalandı. */}
            <span role="status">{copied && t('components.Toast.copied')}</span>
          </div>
        )}{' '}
        {action && (
          <ToastPrimitive.Action altText={action.label} asChild onClick={action.onClick}>
            <Button variant="outline" size="sm">
              {action.label}
            </Button>
          </ToastPrimitive.Action>
        )}
      </div>
      <ToastPrimitive.Close asChild>
        <Button variant="ghost" size="icon" aria-label={t('components.Toast.close')}>
          <Icon icon={X} size="sm" />
        </Button>
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}
